import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionCount,
  SectionContent,
  ExpandableList,
  ExpandableItem,
  ExpandableHeader,
  ExpandableInfo,
  ExpandableInfoWrapper,
  ExpandableSubtitle,
  ExpandableContent,
  ExpandableDetails,
  PropertyDataRow as DataRow,
  DataLabel,
  DataValue,
} from '../../../molecules/PropertyPanelStyles'
import { DataLabelWithTooltip } from '../../../molecules/DataLabelWithTooltip'
import { ExpandArrow } from '../../../atoms/ExpandArrow'
import { Icon } from '../../../atoms/Icon'
import {
  BuildingPopetyIcon,
  getBuildingClassificationConfig,
} from '../../../molecules/BuildingPopetyIcon'
import type { LandBuilding } from '@popety-io/types'
import { getBuildingProtectionStatus } from '@popety-io/utils'
import {
  formatNumber,
  formatNumberSwiss,
} from '../../../../utils/number/formatUtils'

type PropertyPanelBuilding = LandBuilding & {
  popety_classification?: string
  gross_floor_area?: number
}

// Type chip shown as the card title — tinted with the classification colour,
// darkened text tone for legibility, and a solid accent dot.
const TypeChipRow = styled.div`
  display: flex;
  min-width: 0;
  margin-bottom: 4px;
`

const TypeChip = styled.span<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`

const TypeChipDot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $color }) => $color};
`

// Neutral meta item: dark value with a slightly lighter (but still dark) icon.
const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`

interface BuildingsSectionProps {
  buildings: PropertyPanelBuilding[]
}

export const BuildingsSection: React.FC<BuildingsSectionProps> = ({
  buildings,
}) => {
  const { t } = useTranslation('explore')
  const { t: tCommon } = useTranslation('common')
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const toggleExpand = (buildingId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(buildingId)) {
        newSet.delete(buildingId)
      } else {
        newSet.add(buildingId)
      }
      return newSet
    })
  }

  if (!buildings || buildings.length === 0) {
    return null
  }

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 21h18M3 7v14M9 7v14M15 7v14M21 7v14M6 3h12v4H6z" />
            </svg>
          </SectionIcon>
          {t('propertyTooltip.buildings.title')}
        </SectionTitle>
        <SectionCount>{buildings.length}</SectionCount>
      </SectionHeader>
      <SectionContent>
        <ExpandableList>
          {buildings.map((building) => {
            const adminInfo = building.buildings_administrative?.[0]
            const protectionStatus = getBuildingProtectionStatus(building)
            const isExpanded = expandedItems.has(building.id)
            const fullAddress = building.addresses?.[0]
              ? `${building.addresses[0].street || ''} ${building.addresses[0].street_nb || ''}, ${building.addresses[0].postal_code || ''} ${building.addresses[0].city || ''}`.trim()
              : ''
            const classificationConfig = getBuildingClassificationConfig(
              adminInfo?.popety_classification || building.popety_classification
            )

            return (
              <ExpandableItem key={building.id} $expanded={isExpanded}>
                <ExpandableHeader onClick={() => toggleExpand(building.id)}>
                  <ExpandableInfo>
                    <BuildingPopetyIcon
                      classification={
                        (adminInfo?.popety_classification ||
                          building.popety_classification) as any
                      }
                      size={60}
                    />
                    <ExpandableInfoWrapper>
                      <TypeChipRow>
                        <TypeChip
                          $bg={classificationConfig.bg}
                          $color={classificationConfig.textColor}
                        >
                          <TypeChipDot $color={classificationConfig.color} />
                          {t(classificationConfig.i18nKey)}
                        </TypeChip>
                      </TypeChipRow>
                      {fullAddress && (
                        <ExpandableSubtitle>
                          <span>{fullAddress}</span>
                        </ExpandableSubtitle>
                      )}
                      <ExpandableSubtitle style={{ marginTop: '4px' }}>
                        {!!adminInfo?.unit_nb && (
                          <MetaItem>
                            <Icon name="door-open" size="small" />
                            {adminInfo.unit_nb}{' '}
                            {t('propertyTooltip.buildings.units')}
                          </MetaItem>
                        )}
                        {!!adminInfo?.area && (
                          <MetaItem>
                            <Icon name="square" size="small" />
                            {formatNumber(adminInfo.area)} m²
                          </MetaItem>
                        )}
                        {!!building.floor_nb && (
                          <MetaItem>
                            <Icon name="layers" size="small" />
                            {building.floor_nb}{' '}
                            {t('propertyTooltip.buildings.floors')}
                          </MetaItem>
                        )}
                        {!!building.gross_floor_area && (
                          <MetaItem>
                            <Icon name="ruler" size="small" />
                            SB {formatNumber(building.gross_floor_area)} m²
                          </MetaItem>
                        )}
                      </ExpandableSubtitle>
                    </ExpandableInfoWrapper>
                  </ExpandableInfo>
                  <ExpandArrow $expanded={isExpanded} direction="down" />
                </ExpandableHeader>
                <ExpandableContent $expanded={isExpanded}>
                  <ExpandableDetails>
                    {adminInfo && (
                      <>
                        {!!adminInfo.egid && (
                          <DataRow>
                            <DataLabel>EGID</DataLabel>
                            <DataValue>{adminInfo.egid}</DataValue>
                          </DataRow>
                        )}
                        <DataRow>
                          <DataLabelWithTooltip
                            label={t('propertyTooltip.buildings.buildingYear')}
                            explanation={tCommon(
                              'propertyExplanations.buildings.buildingYear'
                            )}
                          >
                            {t('propertyTooltip.buildings.buildingYear')}
                          </DataLabelWithTooltip>
                          <DataValue>
                            {adminInfo.building_year ||
                              adminInfo.building_period ||
                              '-'}
                          </DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabelWithTooltip
                            label={t(
                              'propertyTooltip.buildings.renovationYear'
                            )}
                            explanation={tCommon(
                              'propertyExplanations.buildings.renovationYear'
                            )}
                          >
                            {t('propertyTooltip.buildings.renovationYear')}
                          </DataLabelWithTooltip>
                          <DataValue>
                            {adminInfo.renovation_year ||
                              adminInfo.renovation_period ||
                              '-'}
                          </DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabelWithTooltip
                            label={t('propertyTooltip.buildings.protected')}
                            explanation={tCommon(
                              'propertyExplanations.buildings.protected'
                            )}
                          >
                            {t('propertyTooltip.buildings.protected')}
                          </DataLabelWithTooltip>
                          <DataValue>
                            {protectionStatus === 'protected'
                              ? t('common.yes')
                              : protectionStatus === 'not-protected'
                                ? t('common.no')
                                : tCommon('notAvailable')}
                          </DataValue>
                        </DataRow>
                        {adminInfo.area && (
                          <DataRow>
                            <DataLabelWithTooltip
                              label={t(
                                'propertyTooltip.buildings.groundFloorArea'
                              )}
                              explanation={tCommon(
                                'propertyExplanations.buildings.groundFloorArea'
                              )}
                            >
                              {t('propertyTooltip.buildings.groundFloorArea')}
                            </DataLabelWithTooltip>
                            <DataValue>
                              {formatNumber(adminInfo.area)} m²
                            </DataValue>
                          </DataRow>
                        )}
                        {building.volume_estimate && (
                          <DataRow>
                            <DataLabelWithTooltip
                              label={t('propertyTooltip.buildings.volume')}
                              explanation={tCommon(
                                'propertyExplanations.buildings.volume'
                              )}
                            >
                              {t('propertyTooltip.buildings.volume')}
                            </DataLabelWithTooltip>
                            <DataValue>
                              {formatNumberSwiss(building.volume_estimate)} m³
                            </DataValue>
                          </DataRow>
                        )}
                        {building?.height_estimate && (
                          <DataRow>
                            <DataLabelWithTooltip
                              label={t('propertyTooltip.buildings.maxHeight')}
                              explanation={tCommon(
                                'propertyExplanations.buildings.maxHeight'
                              )}
                            >
                              {t('propertyTooltip.buildings.maxHeight')}
                            </DataLabelWithTooltip>
                            <DataValue>
                              {Number(building.height_estimate).toFixed(2)} m
                            </DataValue>
                          </DataRow>
                        )}
                        {adminInfo.floor_nb && (
                          <DataRow>
                            <DataLabelWithTooltip
                              label={t('propertyTooltip.buildings.floorNumber')}
                              explanation={tCommon(
                                'propertyExplanations.buildings.floorNumber'
                              )}
                            >
                              {t('propertyTooltip.buildings.floorNumber')}
                            </DataLabelWithTooltip>
                            <DataValue>{adminInfo.floor_nb}</DataValue>
                          </DataRow>
                        )}
                        {adminInfo.residential_floor_nb && (
                          <DataRow>
                            <DataLabelWithTooltip
                              label={t(
                                'propertyTooltip.buildings.residentialFloorNumber'
                              )}
                              explanation={tCommon(
                                'propertyExplanations.buildings.residentialFloorNumber'
                              )}
                            >
                              {t(
                                'propertyTooltip.buildings.residentialFloorNumber'
                              )}
                            </DataLabelWithTooltip>
                            <DataValue>
                              {adminInfo.residential_floor_nb}
                            </DataValue>
                          </DataRow>
                        )}
                        {adminInfo.unit_nb && (
                          <DataRow>
                            <DataLabelWithTooltip
                              label={t(
                                'propertyTooltip.buildings.numberOfDwellings'
                              )}
                              explanation={tCommon(
                                'propertyExplanations.buildings.numberOfDwellings'
                              )}
                            >
                              {t('propertyTooltip.buildings.numberOfDwellings')}
                            </DataLabelWithTooltip>
                            <DataValue>{adminInfo.unit_nb}</DataValue>
                          </DataRow>
                        )}
                        {adminInfo.residential_area && (
                          <DataRow>
                            <DataLabelWithTooltip
                              label={t(
                                'propertyTooltip.buildings.totalResidentialArea'
                              )}
                              explanation={tCommon(
                                'propertyExplanations.buildings.totalResidentialArea'
                              )}
                            >
                              {t(
                                'propertyTooltip.buildings.totalResidentialArea'
                              )}
                            </DataLabelWithTooltip>
                            <DataValue>
                              {formatNumber(adminInfo.residential_area)} m²
                            </DataValue>
                          </DataRow>
                        )}
                        {adminInfo.heating_energy_source && (
                          <DataRow>
                            <DataLabelWithTooltip
                              label={t(
                                'propertyTooltip.buildings.heatingEnergySource'
                              )}
                              explanation={tCommon(
                                'propertyExplanations.buildings.heatingEnergySource'
                              )}
                            >
                              {t(
                                'propertyTooltip.buildings.heatingEnergySource'
                              )}
                            </DataLabelWithTooltip>
                            <DataValue style={{ color: 'var(--primary)' }}>
                              {adminInfo.heating_energy_source}
                            </DataValue>
                          </DataRow>
                        )}
                        {adminInfo.idc && (
                          <DataRow>
                            <DataLabelWithTooltip
                              label={t('propertyTooltip.buildings.idc')}
                              explanation={tCommon(
                                'propertyExplanations.buildings.idc'
                              )}
                            >
                              {t('propertyTooltip.buildings.idc')}
                            </DataLabelWithTooltip>
                            <DataValue style={{ color: 'var(--warning)' }}>
                              {adminInfo.idc}{' '}
                              {t('propertyTooltip.buildings.idcUnit')}
                            </DataValue>
                          </DataRow>
                        )}
                        {adminInfo.solar_potential && (
                          <DataRow>
                            <DataLabelWithTooltip
                              label={t(
                                'propertyTooltip.buildings.solarPotential'
                              )}
                              explanation={tCommon(
                                'propertyExplanations.buildings.solarPotential'
                              )}
                            >
                              {t('propertyTooltip.buildings.solarPotential')}
                            </DataLabelWithTooltip>
                            <DataValue>{adminInfo.solar_potential}</DataValue>
                          </DataRow>
                        )}
                      </>
                    )}
                  </ExpandableDetails>
                </ExpandableContent>
              </ExpandableItem>
            )
          })}
        </ExpandableList>
      </SectionContent>
    </InfoSection>
  )
}
